import { usePublicMetadata } from '../seo/PublicMetadata.jsx';
import { useInitialPublicData } from '../seo/PublicBootstrap.jsx';
import { usePublicShareData } from '../hooks/usePublicShareData.js';
import { canMeasureArticle, measureArticle, trackArticleAction } from '../services/contentMeasurement.js';
import { ContentPublicationBody } from '@mts/ui/content-renderer';
import { useEffect, useRef, useState, useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, BadgePercent, Building2, CalendarDays, ChevronRight, Clock, UserRound } from 'lucide-react';
import { PublicContainer, ShareButton } from '@mts/ui';
import { fetchBlogPost } from '../services/content';
import { usePublicTenant } from '../hooks/usePublicTenant';
import PublicOptInPanel from '../components/public/PublicOptInPanel';
import { trackVisitorEvent } from '../services/visitorTracking.js';
import { getSettingValue } from '../utils/homepageHeroSettings';
import { buildPublicPageMetadata } from '../utils/publicMetadata.js';
import { buildArticleSchema, buildBreadcrumbSchema, buildWebPageSchema } from '../utils/structuredData.js';
import NotFoundPage from './NotFoundPage.jsx';
import PublicBreadcrumbs from '../components/common/PublicBreadcrumbs.jsx';

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', day: 'numeric', year: 'numeric' });
}

const EXPLORE_LINKS = [
  { to: '/listings', label: 'Local Businesses', icon: Building2 },
  { to: '/events', label: 'Upcoming Events', icon: CalendarDays },
  { to: '/offers', label: 'Special Offers', icon: BadgePercent },
];

function ExploreTenantCard({ cityName }) {
  return <section aria-labelledby="explore-tenant-title" className="overflow-hidden rounded-xl border border-[var(--border,#e2e8f0)] bg-white shadow-sm">
    <header className="border-b border-[var(--border,#e2e8f0)] bg-[var(--surface,#f8fafc)] px-5 py-4"><h2 id="explore-tenant-title" className="text-lg font-bold text-[var(--text,#0f172a)]">Explore {cityName || 'My Town'}</h2></header>
    <nav aria-label={`Explore ${cityName || 'My Town'}`} className="divide-y divide-[var(--border,#e2e8f0)]">{EXPLORE_LINKS.map(({ to, label, icon: Icon }) => <Link key={to} to={to} className="group flex items-center gap-3 px-5 py-4 text-sm font-semibold text-[var(--text,#0f172a)] transition hover:bg-[var(--surface,#f8fafc)] hover:text-[var(--primary,#1d4ed8)]"><Icon aria-hidden="true" className="h-5 w-5 shrink-0 text-[var(--primary,#1d4ed8)]"/><span className="flex-1">{label}</span><ChevronRight aria-hidden="true" className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5"/></Link>)}</nav>
  </section>;
}

function SupportLocalCard({ cityName }) {
  const resolvedCity = cityName || 'My Town';
  return <section aria-labelledby="support-local-title" className="overflow-hidden rounded-xl bg-[var(--primary,#1d4ed8)] p-6 text-[var(--primary-foreground,#fff)] shadow-sm">
    <h2 id="support-local-title" className="font-display text-2xl font-bold leading-tight">Support Local.<br/>Shop Local.<br/>Love Local.</h2>
    <Link to="/offers" className="mt-6 inline-flex min-h-10 items-center justify-center rounded-md bg-white px-4 py-2 text-sm font-bold text-[var(--primary,#1d4ed8)] transition hover:bg-slate-100">Shop {resolvedCity}</Link>
  </section>;
}

const resolvedSidebarType = (slot) => slot.type === 'PLACEHOLDER'
  ? slot.id === 'sidebar-primary' ? 'EXPLORE' : slot.id === 'sidebar-featured' ? 'SUPPORT_LOCAL' : 'EMPTY'
  : slot.type;

function ArticleSidebar({ slots, cityName }) {
  return <aside aria-label="Article sidebar" className="space-y-5">{slots.filter((slot) => slot.enabled).map((slot) => {
    const type = resolvedSidebarType(slot);
    if (type === 'EXPLORE') return <ExploreTenantCard key={slot.id} cityName={cityName}/>;
    if (type === 'SUPPORT_LOCAL') return <SupportLocalCard key={slot.id} cityName={cityName}/>;
    return null;
  })}</aside>;
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const { tenant, spotlightName, tenantLogoConfig, tenantLogoImageUrl } = usePublicTenant();
  const pageInstance = useRef(null);
  if (pageInstance.current?.slug !== slug) pageInstance.current = { slug, id: null, sent: false };
  const initial = useInitialPublicData("article");
  const [post, setPost] = useState(initial || null);
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (initial) { setPost(initial); setLoading(false); setError(null); return undefined; }
    let active = true;
    setLoading(true);
    setError(null);
    fetchBlogPost(slug)
      .then((document) => {
        if (active) setPost(document);
      })
      .catch((err) => {
        if (active) setError(err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [slug, initial]);

  useEffect(() => {
    if (!post || post.slug !== slug) return;
    const instance = pageInstance.current;
    const send = () => {
      if (instance.sent || !canMeasureArticle(post)) return;
      instance.id ||= globalThis.crypto?.randomUUID?.() || null;
      if (instance.id) void measureArticle({ post, pageId: instance.id }).then(accepted => { if (accepted) instance.sent = true; });
    };
    send(); window.addEventListener('mts:consent:changed',send);
    return () => window.removeEventListener('mts:consent:changed',send);
  }, [post, slug]);

  const trackArticleListingClick = (event) => {
    void trackArticleAction(event, post, pageInstance.current.id);
    const action = event.target.closest?.('[data-listing-action]');
    const card = action?.closest('[data-content-listing-card]');
    const listingId = card?.dataset.listingId;
    if (!post?.id || !action || !listingId || !event.currentTarget.contains(card)) return;
    const eventType = { view: 'listing.card.clicked', phone: 'listing.phone.clicked', website: 'listing.website.clicked', directions: 'listing.directions.clicked' }[action.dataset.listingAction];
    if (eventType) trackVisitorEvent({ eventType, listingId, metadata: { context: 'blog', publicationId: post.id, action: action.dataset.listingAction } });
  };

  const publicContent = useMemo(() => {
    if (!post) return null;
    const canonicalPath = `/blog/${encodeURIComponent(post.slug || slug)}`;
    const description = post.metaDescription || post.excerpt || '';
    const imageUrl = post.socialImage?.publicUrl || post.coverImage?.publicUrl || getSettingValue(tenant, 'website.social_image_url') || getSettingValue(tenant, 'seo.social_image_url');
    const metadata = buildPublicPageMetadata({ tenant, pathname: canonicalPath, canonicalPath, siteName: spotlightName, cityName: tenantLogoConfig?.cityName, logoUrl: tenantLogoImageUrl, title: post.metaTitle || `${post.title} | ${spotlightName}`, description, imageUrl, imageAlt: post.socialImage?.altText || post.coverImage?.altText || post.title, openGraphType: 'article', robots: post.isPreview ? 'noindex,follow' : undefined });
    return { metadata, description, imageUrl };
  }, [post, slug, spotlightName, tenant, tenantLogoConfig?.cityName, tenantLogoImageUrl]);
  const shareData = usePublicShareData({ contentType: 'article', title: post?.title, metadata: publicContent?.metadata, status: post?.status, hidden: false });

  const authorName = post?.authorName || post?.author?.displayName || '';
  usePublicMetadata(() => {
    if (!post) return;
    const { metadata: base, description, imageUrl } = publicContent;
    const author = authorName && post.author?.expertSlug && tenant?.canonicalOrigin ? { '@type': 'Person', name: authorName, jobTitle: post.author.professionalTitle, url: new URL(`/experts/${encodeURIComponent(post.author.expertSlug)}`, tenant.canonicalOrigin).toString(), image: post.author.headshot?.publicUrl } : authorName ? { '@type': 'Person', name: authorName } : undefined;
    base.jsonLd = [buildWebPageSchema({ url: base.canonicalUrl, name: base.title, description }), buildArticleSchema({ url: base.canonicalUrl, headline: post.title, description, datePublished: post.publishedAt, dateModified: post.updatedAt, images: imageUrl, author }), buildBreadcrumbSchema({ url: base.canonicalUrl, items: [{ name: 'Home', url: tenant.canonicalOrigin }, { name: 'Blog', url: `${tenant.canonicalOrigin}/blog` }, { name: post.title, url: base.canonicalUrl }] })];
    return (base);
  }, [publicContent, post, authorName, spotlightName, tenant, tenantLogoImageUrl]);

  if (loading) {
    return (
      <div className="min-h-screen overflow-x-hidden bg-surface py-12 text-text-primary">
        <PublicContainer>
          <div className="rounded-lg border border-[var(--border,#e2e8f0)] bg-white p-8 text-center text-[var(--muted,#64748b)]">Loading article...</div>
        </PublicContainer>
      </div>
    );
  }

  if (error?.status === 404 || (!error && !post)) return <NotFoundPage resource="article" />;
  if (error?.status === 403) return <Navigate to="/access-denied" replace />;

  if (error) {
    return (
      <div className="min-h-screen overflow-x-hidden bg-surface py-12 text-text-primary">
        <PublicContainer>
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-900">Articles are temporarily unavailable. {error.message}</div>
        </PublicContainer>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-surface py-10 text-text-primary">
      <PublicContainer>
        <PublicBreadcrumbs items={[{ name: 'Home', href: '/' }, { name: 'Blog', href: '/blog' }, { name: post.title }]} />
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--primary,#1d4ed8)] hover:underline">
          <ArrowLeft className="h-4 w-4" />
          Back to Blog
        </Link>

        <article className="mt-6" onClick={trackArticleListingClick}>
          <header className="border-b border-[var(--border,#e2e8f0)] pb-6">
            {post.category?.name ? <div className="text-sm font-semibold"><Link to={`/blog/category/${post.category.slug}`} className="text-[var(--primary,#1d4ed8)] hover:underline">{post.category.name}</Link></div> : null}
            <h1 className="mt-4 text-4xl font-bold leading-tight md:text-5xl">{post.title}</h1>
            {shareData ? <ShareButton {...shareData} className="mt-4" /> : null}
            {post.excerpt ? <p className="mt-5 text-lg leading-8 text-[var(--muted,#64748b)]">{post.excerpt}</p> : null}
            {(authorName || post.publishedAt || post.readingTime) ? <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-[var(--muted,#64748b)]">
              {authorName ? <address className="flex items-center gap-2 not-italic"><span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-slate-200">{(post.author?.headshot?.publicUrl || post.author?.avatarUrl) ? <img src={post.author.headshot?.publicUrl || post.author.avatarUrl} alt="" className="h-full w-full object-cover"/> : <UserRound aria-hidden="true" className="h-4 w-4 text-slate-500"/>}</span>{post.author?.expertSlug ? <Link to={`/experts/${post.author.expertSlug}`} className="font-semibold text-[var(--text,#0f172a)] hover:text-[var(--primary)] hover:underline">{authorName}</Link> : <span className="font-semibold text-[var(--text,#0f172a)]">{authorName}</span>}</address> : null}
              {authorName && (post.publishedAt || post.readingTime) ? <span aria-hidden="true" className="text-slate-400">|</span> : null}
              {post.publishedAt ? <time dateTime={post.publishedAt} className="font-normal">{formatDate(post.publishedAt)}</time> : null}
              {post.publishedAt && post.readingTime ? <span aria-hidden="true" className="text-slate-400">|</span> : null}
              {post.readingTime ? <span className="inline-flex items-center gap-2 whitespace-nowrap font-semibold"><Clock className="h-4 w-4"/>{post.readingTime} min read</span> : null}
            </div> : null}
            {post.author?.disclosureText ? <p className="mt-4 max-w-2xl rounded-md bg-slate-50 p-3 text-sm text-slate-600">{post.author.disclosureText}</p> : null}
          </header>
          {post.businessContext?.publicPath ? <section aria-label="Associated business" className="mt-6 rounded-lg border bg-slate-50 p-4"><h2 className="text-sm font-semibold text-slate-600">Business in this story</h2><Link to={post.businessContext.publicPath} className="mt-1 inline-block font-semibold text-[var(--primary)] hover:underline">{post.businessContext.name}</Link></section> : null}

          <div className="mt-10 grid min-w-0 gap-10 lg:grid-cols-[minmax(0,760px)_minmax(300px,1fr)] lg:gap-12"><ContentPublicationBody showIssues={import.meta.env.DEV} value={post.contentJson} sanitizedHtml={post.contentHtml} displayContext={{ locale: getSettingValue(tenant, 'tenant.locale') || tenant?.locale || 'en-US', timezone: getSettingValue(tenant, 'tenant.timezone') || tenant?.timezone || 'UTC' }} className="mts-content-body m-0 min-w-0 max-w-none break-words bg-white p-0 prose-headings:text-[var(--text,#0f172a)] prose-a:text-[var(--primary,#1d4ed8)] prose-img:max-w-full prose-pre:max-w-full prose-pre:overflow-x-auto"/><ArticleSidebar slots={post.sidebarConfig?.slots || []} cityName={tenantLogoConfig?.cityName}/></div>
          {post.localGuideJson?.sources?.length ? <section aria-label="Guide sources" className="mt-8 rounded-lg border p-5"><h2 className="text-xl font-semibold">Sources</h2><ul className="mt-3 space-y-2">{post.localGuideJson.sources.map((source, index) => <li key={index}><a className="text-[var(--primary)] hover:underline" href={source.url} rel="noopener noreferrer">{source.label}</a></li>)}</ul></section> : null}
          {post.tags?.length ? <section aria-label="Article tags" className="mt-10 flex flex-wrap gap-2">{post.tags.map((tag) => <span key={tag.id} className="rounded-full bg-slate-200 px-3 py-1 text-sm font-medium text-slate-700">{tag.name}</span>)}</section> : null}
        </article>
        <PublicOptInPanel source="article_opt_in" className="mt-12"/>
      </PublicContainer>
    </div>
  );
}
